import fileDownload from 'js-file-download';
import React from 'react';
import type {
  ItemAssignmentSimpleResponse,
  ItemAssignmentStateEnum} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {
  useExportItemStatusReportMutation,
} from 'src/redux/queries/itemDefinition';
import {useGetItemAssignmentListQuery} from 'src/redux/queries/itemAssignment';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  HandleItemAssignmentRowDetailRender,
  HandleItemAssignmentRowSubTextRender,
  ItemAssignmentPillConfig,
} from './itemAssignmentHelpers';

const maxPageRows = 10;
export interface ItemAssignmentsListByUserProps {
  assignedToUserId: number;
  titleText?: string;
  states?: ItemAssignmentStateEnum[];
  onNavigateTo: (itemDefinitionId: string, id: string) => void;
  onRequestItems?: () => void;
  hasRequestItemsPermission?: boolean;
}

export const ItemAssignmentsListByUser: React.FC<
  ItemAssignmentsListByUserProps
> = ({
  titleText,
  assignedToUserId,
  states,
  onNavigateTo,
  onRequestItems,
  hasRequestItemsPermission = false,
}) => {
  const {setAlert} = useAlert();

  const {hasPermission: hasExportPermission} = useHasModulePermission({
    module: ModuleEnum.ItemTrackerAssignments,
    operation: PermissionOperationEnum.Export,
  });
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `item-assignments-by-user-${assignedToUserId}`,
    pageSize: maxPageRows,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const {data, isLoading} = useGetItemAssignmentListQuery({
    start,
    length,
    assignedToUserId: Number(assignedToUserId),
    search: debouncedSearchTerm,
    states: states,
  });

  const indexListData: ItemAssignmentSimpleResponse[] = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const [triggerExportItemStatusReport] = useExportItemStatusReportMutation();

  const handleNavigateToAssignment = (id: any, row: any) =>
    onNavigateTo(row.itemDefinitionId, id);

  const exportAssignment = async () => {
    try {
      const response = await triggerExportItemStatusReport({
        userId: +assignedToUserId,
      }).unwrap();

      fileDownload(response!.data, `item_for_${assignedToUserId}_export.csv`);
      setAlert('Your file has been successfully exported. ', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <PageHeader>
        <PageHeader.Title>{titleText ?? 'Item Assignments'}</PageHeader.Title>
        <PageHeader.Search
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search..."
        />
        <PageHeader.Actions>
          {onRequestItems && hasRequestItemsPermission && (
            <PageHeader.Action variant="create" onClick={onRequestItems}>
              Request Items
            </PageHeader.Action>
          )}
          {hasExportPermission && (
            <PageHeader.Action variant="export" onClick={exportAssignment} />
          )}
        </PageHeader.Actions>
      </PageHeader>
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={undefined}
        currentPage={currentPage}
        recordsPerPage={maxPageRows}
        totalRecords={totalRecords}
        onRowTextRender={(item: ItemAssignmentSimpleResponse) =>
          item.itemDefinition?.name ?? 'Unknown Item'
        }
        onRowClick={handleNavigateToAssignment}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: ItemAssignmentSimpleResponse) =>
          ItemAssignmentPillConfig(item)
        }
        onRowSubTextRender={HandleItemAssignmentRowSubTextRender}
        onRenderDetailRowNode={HandleItemAssignmentRowDetailRender}
        hasCreationLogic={false}
      />
    </>
  );
};
